/* legend.ts */

/* Utils */
import { capitalizeFirstLetter } from "src/utils/helper/string-transformation";
import {
  getColorByIndex,
  getCohortGroupColor,
} from "src/utils/helper/charts/colorMapper";

/* Constants */
import {
  excludeCohortNoValueGroups,
  orderCohortGroups,
  NOT_LOOKED_FOR_LABEL,
  LOOKED_FOR_NO_VALUE_LABEL,
  HAS_VALUE_LABEL,
} from "src/shared/constants/charts/NoValueStates";

/* Interfaces */
import { LegendProps } from "src/shared/interfaces/charts/Legend";
import { BasicChartProps } from "src/shared/interfaces/charts/BaseChart";
import { ChartType } from "src/shared/constants/charts/Chart";
import { Insights } from "src/shared/constants/common/Common";

/**
 * Single-group (no cohort breakdown) case: the pie would otherwise be one
 * uninformative 100% slice. Split it into the has-value slice plus grey
 * "Not looked for" / "Looked for, no value" completeness slices, matching the
 * grey no-value bars in the chart, so the pie communicates completeness.
 */
const buildCompletenessLegend = (
  type: ChartType,
  group: BasicChartProps
): LegendProps[] => {
  const hasNoValueEntries =
    (group.notLookedForCount ?? 0) +
      (group.lookedForNoValueCount ?? 0) >
    0;

  const slices: LegendProps[] = [
    {
      type: type,
      total: group.total,
      // Only name the has-value state ("With value") when no-value states
      // actually sit next to it - then "Full sample" would mislead (the full
      // sample also contains them). With nothing missing, keep "Full sample".
      name: hasNoValueEntries
        ? HAS_VALUE_LABEL
        : capitalizeFirstLetter(group?.name),
      color: getColorByIndex(0),
    },
  ];

  // Ordered by information quality: "looked for, no value" (assessed, none
  // found) before "not looked for" (never assessed).
  const noValueSlices = [
    {
      name: LOOKED_FOR_NO_VALUE_LABEL,
      total: group.lookedForNoValueCount ?? 0,
    },
    {
      name: NOT_LOOKED_FOR_LABEL,
      total: group.notLookedForCount ?? 0,
    },
  ];

  noValueSlices.forEach((slice) => {
    if (slice.total > 0) {
      slices.push({
        type: type,
        total: slice.total,
        name: slice.name,
        color: getCohortGroupColor(slice.name, 0),
      });
    }
  });

  return slices;
};

export const createChartLegendInfo = (
  type: ChartType,
  data: BasicChartProps[],
  showNoValueEntries?: boolean
): LegendProps[] => {
  const filteredData = orderCohortGroups(
    excludeCohortNoValueGroups(data, showNoValueEntries ?? false)
  );

  // With no cohort breakdown (the lone "Full sample" group) and the toggle on,
  // split that single slice into value + grey no-value slices instead of a
  // single 100% slice. Guard on the group actually being the full sample, not
  // just `length === 1`: a real cohort breakdown that filters down to one
  // populated group (e.g. only "Female" remains) must keep showing that
  // cohort's name, not be relabeled to "With value".
  if (
    showNoValueEntries &&
    filteredData.length === 1 &&
    filteredData[0].name === Insights.FULL_SAMPLE
  ) {
    return buildCompletenessLegend(type, filteredData[0]);
  }

  return filteredData.map((data, index) => {
    // The pie chart (Tortendiagramm) shows the cohort distribution - one slice
    // per cohort group. `total` alone only counts patients with an actual
    // analyzed-feature value, so cohort groups whose members are all "not
    // looked for"/"looked for, no value" for the feature would be invisible
    // (0-sized slices) and real groups would under-count. When no-value entries
    // are shown, size each slice by the full patient count of the cohort group
    // so every group - including the "Not looked for"/"Looked for, no value"
    // cohort groups - is represented with the correct percentage.
    const total = showNoValueEntries
      ? data.total +
        (data.notLookedForCount ?? 0) +
        (data.lookedForNoValueCount ?? 0)
      : data.total;

    // The lone "Full sample" slice (no cohort) actually shows only the
    // has-value patients - the no-value ones are hidden with the toggle off.
    // When such hidden entries exist, name it "With value" so the label is
    // honest and hints they exist; a genuinely complete sample keeps its
    // "Full sample" name.
    const hasHiddenNoValueEntries =
      (data.notLookedForCount ?? 0) +
        (data.lookedForNoValueCount ?? 0) >
      0;
    const name =
      data?.name === Insights.FULL_SAMPLE &&
      hasHiddenNoValueEntries
        ? HAS_VALUE_LABEL
        : capitalizeFirstLetter(data?.name);

    return {
      type: type,
      total: total,
      name: name,
      cohort: "",
      unit: "",
      color: getCohortGroupColor(data?.name, index),
    };
  });
};
